import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useOutletContext } from 'react-router-dom';
import { useToggle } from 'react-use';
import { icons } from '../assets';
import {
  ActionFilters,
  BigButton,
  FooterButton,
  GlobalFilter,
  Loader,
  ModalButtonArea,
  SharePopup,
  TopButtonArea,
} from '../common';
import AddToCollectionPopup from '../common/AddToCollectionPopup';
import FilterSideModalButton from '../common/FilterSideModalButton';
import { GlobalDefaultTeamMemberFilter, GlobalSearchTagsFilter } from "../common/GlobalFilter";
import PageTitle from '../common/PageTitle';
import RendersRestorePopup from '../common/RendersRestorePopup';
import { ThumbnailContainerV2 } from '../common/ThumbnailContainerV2';
import { PageSearchBar } from '../common/UniversalSearchForm/UniversalSearchForm';
import { UploadImagesDialog } from '../common/UploadImagesDialog/UploadImagesDialog';
import BinIcon from '../components/icons/BinIcon';
import InfiniteScroll from '../components/InfiniteScroll/InfiniteScroll';
import MyDropzone from '../components/MyDropzone';
import { COMMON_TAGS_MODEL_TYPES, MODEL_TYPES, MODULE_TYPE_OPTIONS, LIFESTYLE_ROOM_VALUE, RENDER_STATUS } from '../constants/constants';
import { DataListContextProvider } from '../contexts/DataListContext';
import { useUserContext } from '../contexts/UserContext';
import {
  apiArchiveRenders,
  apiGetDownloadRendersImagesV2,
  apiPostImageOnPresignedData,
  apiRestoreMutipleArchivedRenders,
} from '../helpers/api';
import { useScreenSettings } from '../helpers/configureHelper';
import { setTitle, totalItemCount  } from '../helpers/jsHelper';
import { errorToast, warningToast, successToast } from '../helpers/toastHelper';
import { getParamsFromFilterBy } from '../helpers/urlHelper';
import { useInfoDialogaActions } from '../hooks/useInfoDialogaActions';
import { CommonGrid, TopContainer } from '../layouts';
import FooterOptions from '../layouts/FooterOptions';
import TopContainerV2 from '../layouts/TopContainerV2';
import { useRendersPreviews } from '../pages/admin/renders-previews/useRendersPreviews';
import { updateProductRenderJsonDataAPI } from '../api/renderPreview';
import { addUpdateParamsInSequence } from '../redux/slicers/filters/filterSlicer';
import {
  myRenderPreviewActions,
  myRenderPreviewSelectors,
  selectUpdateJsonModalData,
} from '../redux/slicers/render_preview/myRenderPreviewSlice';
import { getFilterOptionsHelper } from '../helpers/dataHelper';
import { useDownloadEntity } from '../hooks/useDownloadEntity.js';
import { MessageBar } from './subscriptionComponents/MessageBar.js';
import { ButtonV2 } from '../components/ButtonV2/ButtonV2.js';
import { configuratorActions, selectShowFullScreenLoader } from '../redux/slicers/configurator/configurator.slicer.js';
const { UploadIcon, CollectionIcon, DownloadIcon, ArchiveIcon, File, Close } = icons;

let title = 'Images';

export default function RendersPreviews({
  productId = '',
  cardType = 'renders-previews',
  useGlobalSearch,
  modelType = MODEL_TYPES.PROJECTRENDER,
  noDataText = modelType === MODEL_TYPES.PROJECTRENDER ? "Your images live here!" : "",
  defaultFilterBy,
  cardProps,
  updatePageTitle = true,
  membersFilterDefaultSelected = '', // 'me',
  apiConfig = {},
  children,
  designV2,
  singleSelect,
  popupCardsSelected,
  is360Configurator,
  hideStickyHeader = null,
  skeletonContainerClass,
  redirect = true,
  showMenu = true,
  useGlobalFilter = true
}) {
  const dispatch = useDispatch();
  const showFullScreenLoader = useSelector(selectShowFullScreenLoader);
  const currentRenderJsonData = useSelector(selectUpdateJsonModalData)
  const { openInfoDialog, closeInfoDialog } = useInfoDialogaActions();
  const { setAngleImageUrl } = useDownloadEntity()
  const { currentUser } = useUserContext();
  const currentMemberId = currentUser.id;
  const [filterByForLightroom, setFilterByForLightroom] = useState('');

  const [showUploadDialog, toggleShowUploadDialog] = useToggle(false);
  const pageSettings = useScreenSettings(cardType);
  const { sortOptions, filterOptions, defaultSortOption, showFavoriteIcon, showTeamMemberFilter } =
    pageSettings;

    // This will disable all action and remove action button on cards (currently used in configurator lighting)
    let disableRenderPreviewCardAction;
  if (!redirect && !showMenu) {
    disableRenderPreviewCardAction = true;
  }

  const updatedFilterOptions = getFilterOptionsHelper(filterOptions, disableRenderPreviewCardAction, is360Configurator);

  const selectedFiles = useMemo(() => {
    if (!currentRenderJsonData?.jsonFile) return [];
    return [
      {
        id: currentRenderJsonData?.id || currentRenderJsonData?.jsonFile?.name || 'json-file',
        name: currentRenderJsonData?.jsonFile?.name || 'file.json',
      },
    ];
  }, [currentRenderJsonData?.id, currentRenderJsonData?.jsonFile]);

  const handleJsonFileDrop = (files = []) => {
    const file = files?.[0] || null;
    dispatch(
      myRenderPreviewActions.setUpdateJsonModalData({
        ...(currentRenderJsonData || {}),
        jsonFile: file,
      })
    );
  };

  const handleFileDelete = () => {
    dispatch(
      myRenderPreviewActions.setUpdateJsonModalData({
        ...(currentRenderJsonData || {}),
        jsonFile: null,
      })
    );
  };

  const checkForDisable = () => selectedFiles.length === 0;

  const handleFileImport = async () => {
    const selectedFile = currentRenderJsonData?.jsonFile;
    const selectedFileName = currentRenderJsonData?.jsonFile?.name;
    const selectedRenderId = currentRenderJsonData?.id;

    if (!selectedRenderId) {
      errorToast('Render id is missing.');
      return;
    }

    if (!selectedFile) {
      warningToast('Please select a json file.');
      return;
    }

    const payload = {
      json_file_name: selectedFileName,
    };

    const blob = new Blob([selectedFile], { type: 'application/json' });

    try {
      dispatch(configuratorActions.setShowFullScreenLoader(true))
      const response = await updateProductRenderJsonDataAPI({
        id: selectedRenderId,
        payload,
      });

      if (!response?.presigned_url) {
        errorToast('Unable to get upload URL for json file.');
        return;
      }

      await apiPostImageOnPresignedData(blob, response?.presigned_data);
      successToast('Json file uploaded successfully.');
      refetchList();
    } catch (error) {
      console.error('Error uploading render json file:', error);
      errorToast('Unable to upload json file. Please try again.');
    } finally {
      dispatch(configuratorActions.setShowFullScreenLoader(false));
      dispatch(myRenderPreviewActions.setUpdateJsonModalData(null));
    }
  };


  useEffect(() => {
    if(updatePageTitle) {
      setTitle(title);
    }
    return (() => {
      setTimeout(() => {
        window.$restrictFilter = null
      },1000)
    })
  }, []);

  const requestParams = useMemo(
    () => ({
      created_by: '',
      is_configurator: false,
      ...(productId && { product: productId }),
      ...apiConfig.requestParams,
      ...(disableRenderPreviewCardAction && filterByForLightroom)
    }),
    [currentMemberId, productId, apiConfig.requestParams, filterByForLightroom]
  );

  const {
    list: _list,
    loading,
    fetchMoreData,
    hasMore,
    total,
    hasData,

    selected,
    anySelected,
    allSelected,
    selectedList,
    sortBy,
    filterBy,
    onSortChange,
    onFilterByChange,
    toggleAll,
    toggleItem,
    toggleSingleItem,
    resetSelection,
    addToCollection,
    refetchList,
    search,
    onSearchChange,
  } = useRendersPreviews({
    requestParams,
    defaultFilterBy,
    defaultSort: defaultSortOption,
    useGlobalSearch,
    modelType: MODEL_TYPES.PROJECTRENDER,
    useGlobalFilter,
  });

  const list = _list.map(val => {
    const changeName = val?.images?.length && (val?.product_texture_render || val?.product_model_render);
    const name = changeName ? `${val.name}_${val?.images?.[0]?.angle_name}` : val.name;
    return { ...val, name };
  });

  useEffect(() => {
    if (disableRenderPreviewCardAction) {
      popupCardsSelected({anySelected, allSelected, selected, selectedList})
    }
  }, [disableRenderPreviewCardAction, selected])

  useEffect(() => {
    // when user delete single item then I call the refetch API again.
    if (window?.$refetch) {
      refetchList();
      window.$refetch = false;
    }
  }, [list]);

  const [isDownloading, setIsDownloading] = useState(false);
  const handleUploadSuccess = (renderData) => {
    if (
      !filterBy ||
      filterBy.value === MODULE_TYPE_OPTIONS.RENDERS ||
      filterBy.value === MODULE_TYPE_OPTIONS.UPLOADS
    ) {
      dispatch(myRenderPreviewActions.addSuccess(renderData));
    }
    toggleShowUploadDialog();
  };

  const shareMultiSelectedList = useMemo(
    () => selectedList?.filter((a) => !(a?.is_product_builder && a?.type === '9')), // Temporary hide: Hide share option for product builder renders with Silo template (type '9')
    [selectedList]
  );

  async function handleDownloadClick() {
    let selectedIds = [...selected];

    if (selectedIds.length > 50) {
      warningToast('You are not allowed to download more than 50 images.');
      return;
    }

    if (selectedIds.length < 1) return;

    let fileName = 'render.zip';

    if (selectedIds.length === 1) {
      const jobObj = selectedList?.find((a) => a.id === selectedIds?.[0]);
      fileName = `${jobObj?.name}.zip`;
      downloadMultipleImages({ selectedIds, fileName, is_separate: true });
      return;
    }

    openInfoDialog({
      title: 'Do you want a separate folder for each template?',
      cancelButtonText: 'No',
      cancelBtnColor: 'outline-dark',
      okBtnColor: 'warning',
      buttonText: 'Yes',
      onButtonClick: () => {
        downloadMultipleImages({ selectedIds, fileName, is_separate: true });
        closeInfoDialog();
      },
      onCancelClick: () => downloadMultipleImages({ selectedIds, fileName, is_separate: false }),
    });
  }

  const downloadMultipleImages = async ({ selectedIds, fileName, is_separate }) => {
    setIsDownloading(true);
    successToast('Please wait while we prepare your images.');
    // await apiGetDownloadRendersImagesV2({ ids: selectedIds, fileName: fileName, is_separate });
    await setAngleImageUrl({ selectedList: selectedList, fileName: fileName, is_separate })
    setIsDownloading(false);
  };

  const isFilteredByRender = filterBy?.value === MODULE_TYPE_OPTIONS.RENDERS;
  const isSingleRenderSelected = selectedList.length === 1 && selectedList[0].is_main_render;
  const isSinglePreviewSelected = selectedList.length === 1 && selectedList[0].is_preview;
  const deleteDialogTitle = isSingleRenderSelected
    ? 'Are you sure you want to archive this render?'
    : isSinglePreviewSelected
    ? 'Are you sure you want to delete this preview ?'
    : 'Delete selected?';
  const deleteDialogSubitle = isSingleRenderSelected
    ? 'You can view the archived renders through filter panel.'
    : isSinglePreviewSelected
    ? 'Deleted items will be moved to trash.'
    : 'Rendered images will be archived & rest will be deleted.';

  const isPreviewSelected = useMemo(() => {
    if (selectedList?.length === 1) {
      return selectedList?.some((a) => a?.is_preview === true);
    }

    return filterBy?.value === MODULE_TYPE_OPTIONS.PREVIEWS;
  }, [selectedList]);

  const isRenderStatusDone = useMemo(() => {
    if (!selectedList?.length) return false;

    return selectedList?.every(
      ({ status }) => status === RENDER_STATUS.STATUS_DONE
    );
  }, [selectedList]);

  const isRenderSelected = isFilteredByRender || isSingleRenderSelected;

  const handleDeleteClick = () => {
    openInfoDialog({
      title: deleteDialogTitle,
      description: deleteDialogSubitle,
      cancelButtonText: 'Cancel',
      cancelBtnColor: 'warning',
      okBtnColor: 'outline-dark',
      buttonText: isRenderSelected ? 'Archive' : 'Delete',
      onButtonClick: async () => {
        const onDeleteFailed = (failedItems) => {
          if (failedItems.length !== selected.length) {
            refetchList();
          }
        };
        try {
         const apiCalls = selected.map((id) =>
            apiArchiveRenders({ idList: [id], onDeleteFailed ,  showSuccessMessage:false })
          );
          const results = await Promise.all(apiCalls);
          const allSuccess = results.every(result => result?.status);
      
          if (allSuccess) {
            successToast('These renders have been successfully archived');
            refetchList();
          } else {
            // Optional: handle some failed cases
            refetchList(); // Still refetch, since some may be successful
          }
        } catch (error) {
          console.error("Error archiving renders:", error);
        }
      },
    });
  };

  const handleFilterByChange = (value) => {
    if (!disableRenderPreviewCardAction) {
      dispatch(addUpdateParamsInSequence({ filterBy: getParamsFromFilterBy(value) }));
    } else {
      setFilterByForLightroom({ [value.key]: value.apiValue });
    }
    onFilterByChange(value);
  };

  const NoDataFound = () => {
    return (
      <div className="w-100 m-auto d-flex flex-column justify-content-center align-items-center">
        <h2>{noDataText}</h2>
        <p>All the product visuals you create on imagine will be</p>
        <p>available here.</p>
      </div>
    );
  };

  return (
    <>
    <CommonGrid
      sticky={hideStickyHeader ? false : true}
      noDataText={
        search
          ? `You don't have any ${
              cardType === 'renders-previews' ? 'images' : cardType
            } with "${search}" name.`
          : NoDataFound()
      }
      {...(designV2
        ? {
            topContainer: (
              <>
                <TopContainerV2
                  subClassName={`myFilesFilterContainer ${
                    disableRenderPreviewCardAction ? 'extraSmallCards' : ''
                  }`}
                  globalFilter={
                    !useGlobalSearch && (
                      <div>
                        <GlobalFilter designV2 />
                        <GlobalSearchTagsFilter designV2={designV2} />
                        <GlobalDefaultTeamMemberFilter designV2 />
                      </div>
                    )
                  }>
                  <>
                    {!useGlobalSearch && (
                      <>
                        {!disableRenderPreviewCardAction && (
                          <div className="totalCount">{totalItemCount(total)}</div>
                        )}
                        <PageSearchBar
                          designV2
                          model={COMMON_TAGS_MODEL_TYPES.PROJECTRENDER}
                          onSearchChange={onSearchChange}
                          search={search}
                        />
                        {!disableRenderPreviewCardAction && (
                          <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                        )}
                        <ActionFilters
                          loading={loading}
                          filterProps={
                            // (loading || list?.length || filterBy) &&
                            {
                              value: filterBy,
                              data: updatedFilterOptions,
                              onChange: handleFilterByChange,
                              containerClass: 'top-filter',
                            }
                          }
                          sortProps={
                            // (loading || list?.length > 0) &&
                            {
                              value: sortBy,
                              data: sortOptions,
                              onChange: onSortChange,
                              containerClass: 'top-filter',
                            }
                          }
                          showTeamMemberFilter={false}
                          membersFilterDefaultSelected={membersFilterDefaultSelected}
                        />
                      </>
                    )}
                  </>
                </TopContainerV2>
              </>
            ),
          }
        : {
            topContainer: (
              <TopContainer
                title={
                  !useGlobalSearch && <PageTitle title={title} total={total} search={search} />
                }
                item={cardType}
                hasData={hasData}
                buttonArea={
                  <div style={{ display: 'flex' }}>
                    {!useGlobalSearch && (
                      <TopButtonArea
                        hasData={hasData}
                        item={cardType}
                        addNewButtonArea={
                          null
                          // !isPreviewSelected && (
                          //   <BigButton className="lf-btn-1" buttonIcon={UploadIcon} onClick={() => toggleShowUploadDialog()} buttonTitle="Upload" />
                          // )
                        }
                        topButtonList={
                          !useGlobalSearch && filterBy?.value === 'ARCHIVED'
                            ? [
                                {
                                  element: RendersRestorePopup,
                                  bigButton: true,
                                  bigBtnClass: 'lf-btn-3',
                                  item: cardType,
                                  onSuccess: refetchList,
                                  actionWord: 'restore',
                                  deleteAction: apiRestoreMutipleArchivedRenders,
                                },
                              ]
                            : [
                                {
                                  element: BigButton,
                                  iconBefore: isRenderSelected ? <ArchiveIcon /> : <BinIcon />,
                                  bigButton: true,
                                  buttonTitle: isRenderSelected ? 'Archive' : 'Delete',
                                  bigBtnClass: 'lf-btn-3',
                                  onClick: handleDeleteClick,
                                },
                                {
                                  element: BigButton,
                                  bigBtnClass: 'lf-btn-2',
                                  iconBefore: <CollectionIcon />,
                                  buttonTitle: 'Add To Collection',
                                  onClick: addToCollection,
                                },
                                {
                                  element: SharePopup,
                                  bigButton: shareMultiSelectedList?.length > 0,
                                  bigBtnClass: 'lf-btn-2',
                                  selectedList: shareMultiSelectedList,
                                  modelType: 6,
                                  myName: 'jatin',
                                },
                                {
                                  element: BigButton,
                                  // element: isPreviewSelected ? null : BigButton,
                                  bigBtnClass: 'lf-btn-1',
                                  isLoading: isDownloading,
                                  buttonTitle: 'Download',
                                  iconBefore: <DownloadIcon />,
                                  onClick: handleDownloadClick,
                                },
                              ]
                        }
                        anySelected={anySelected}
                        selectedList={selectedList}
                      />
                    )}
                    {!useGlobalSearch && (
                      <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                    )}
                  </div>
                }
                searchBar={
                  !useGlobalSearch && (
                    <div className="mt-2">
                      <PageSearchBar
                        model={COMMON_TAGS_MODEL_TYPES.PROJECTRENDER}
                        onSearchChange={onSearchChange}
                        search={search}
                      />
                    </div>
                  )
                }
              />
            ),
          })}
      item={cardType}
      className={anySelected || allSelected ? 'show-action-footer' : ''}
      hasData={list.length > 0 || loading}
      list={list}
      {...(!designV2 &&
        !useGlobalSearch && {
          filterArea: (
            <>
              <ActionFilters
                loading={loading}
                filterProps={
                  // (loading || list?.length || filterBy) &&
                  {
                    value: filterBy,
                    data: updatedFilterOptions,
                    onChange: handleFilterByChange,
                  }
                }
                sortProps={
                  // (loading || list?.length > 0) &&
                  {
                    value: sortBy,
                    data: sortOptions,
                    onChange: onSortChange,
                  }
                }
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
                showTeamMemberFilter={false}
                membersFilterDefaultSelected={membersFilterDefaultSelected}
              />
              <GlobalFilter />
            </>
          ),
        })}>
      <InfiniteScroll
        dataLength={list.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loading={loading}
        hasChildren={hasData}
        showMenu={showMenu}
        redirect={redirect}
        skeletonContainerClass={skeletonContainerClass}
        scrollableTarget="infiniteScroll">
        <ThumbnailContainerV2
          items={list}
          modelType={modelType}
          selected={selected}
          designV2={designV2}
          showMenu={showMenu}
          redirect={redirect}
          cardProps={{
            fullWidth: true,
            onSelect: singleSelect ? toggleSingleItem : toggleItem,
            resetSelection:resetSelection,
            refetchList: refetchList,
            renderPreviews: modelType === MODEL_TYPES.PROJECTRENDER,
            ...cardProps,
          }}
        />
        {hasMore && !disableRenderPreviewCardAction && <div id="whiteShadow" />}
      </InfiniteScroll>

      {(anySelected || allSelected) && designV2 && !disableRenderPreviewCardAction && (
        <FooterOptions>
          <>
            <div className="selected-item">
              <ActionFilters
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
              />
              <span>{selectedList?.length || 0} Selected</span>
            </div>
            <div className="footer-action">
              <TopButtonArea
                hasData={hasData}
                item={cardType}
                addNewButtonArea={
                  null
                  // !isPreviewSelected && (
                  //   <BigButton className="lf-btn-1" buttonIcon={UploadIcon} onClick={() => toggleShowUploadDialog()} buttonTitle="Upload" />
                  // )
                }
                topButtonList={
                  !useGlobalSearch && filterBy?.value === 'ARCHIVED'
                    ? [
                        {
                          element: RendersRestorePopup,
                          bigButton: true,
                          bigBtnClass: 'lf-btn-3',
                          item: cardType,
                          onSuccess: refetchList,
                          actionWord: 'restore',
                          deleteAction: apiRestoreMutipleArchivedRenders,
                        },
                      ]
                    : [
                        isRenderStatusDone && {
                          element: BigButton,
                          iconBefore: isRenderSelected ? <ArchiveIcon /> : <BinIcon />,
                          bigButton: true,
                          buttonTitle: isRenderSelected ? 'Archive' : 'Delete',
                          bigBtnClass: 'lf-btn-3',
                          onClick: handleDeleteClick,
                        },
                        {
                          element: BigButton,
                          bigBtnClass: 'lf-btn-2',
                          iconBefore: <CollectionIcon />,
                          buttonTitle: 'Add To Collection',
                          onClick: addToCollection,
                        },
                        isRenderStatusDone && {
                          element: SharePopup,
                          bigButton: shareMultiSelectedList?.length > 0,
                          bigBtnClass: 'lf-btn-2',
                          selectedList: shareMultiSelectedList,
                          modelType: 6,
                        },
                        isRenderStatusDone && {
                          element: BigButton,
                          // element: isPreviewSelected ? null : BigButton,
                          bigBtnClass: 'lf-btn-1',
                          isLoading: isDownloading,
                          buttonTitle: 'Download',
                          iconBefore: <DownloadIcon />,
                          onClick: handleDownloadClick,
                        },
                      ].filter(Boolean)
                }
                anySelected={anySelected}
                selectedList={selectedList}
              />
            </div>
          </>
        </FooterOptions>
      )}

      <UploadImagesDialog
        show={showUploadDialog}
        onClose={() => toggleShowUploadDialog()}
        onSucces={handleUploadSuccess}
      />

      <AddToCollectionPopup onSuccess={resetSelection} />

      <DataListContextProvider
        fetchMoreData={fetchMoreData}
        hasMore={hasMore}
        list={list}
        selectList={myRenderPreviewSelectors.selectList}
        refetchList={refetchList}>
        {!useGlobalSearch && <Outlet />}
        {children}
      </DataListContextProvider>
    </CommonGrid>

    {showFullScreenLoader && (
      <Loader style={{zIndex: 1200}} position="fixed" backdrop loading={true} />
    )}

    <ModalButtonArea
      showModal={Boolean(currentRenderJsonData)}
      setShowModal={() => dispatch(myRenderPreviewActions.setUpdateJsonModalData(null))}
      title={<h4>Update Json</h4>}
      className="upload-texture-modal"
      centered
      size="md"
      headingLeft
      body={
        <>
          <MyDropzone
            isSingleAllowed
            accept="application/json,.json"
            restrictMultiple={true}
            v2UploadTextures
            subTitle="(.json file is supported)"
            onDrop={handleJsonFileDrop}
          />
          
          <MessageBar 
            style={{
              borderColor: '#fef3c6',
              background: '#fffbeb',
              borderRadius: '.5rem',
              margin: '1rem 0'
            }}
            titleStyle={{
              color: '#973c00'
            }}
            text="This will override the existing image data, do you want to proceed?"
          />

          {selectedFiles.length > 0 && (
            <Stack>
              <div
                style={{
                  marginBottom: '1rem',
                  position: 'relative',
                  maxHeight: 225,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                }}>
                <table className="bordered">
                  <tbody>
                    {selectedFiles.map((file, index) => (
                      <tr key={index}>
                        <td>
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}>
                            <File className="me-2" /> {file.name}
                          </span>
                          <span onClick={() => handleFileDelete(file.id)} className="close-upload-file">
                            <Close />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Stack>
          )}


          <div className="file-selected-upload">
            <p>{selectedFiles.length} file selected</p>
            <Stack direction='horizontal' gap={2}>
              <ButtonV2 
                style={{ fontWeight: '500' }} 
                color='dark-75Color'
                size="sm"
                onClick={() => dispatch(myRenderPreviewActions.setUpdateJsonModalData(null))} 
                variant="transparent"
              >
                No
              </ButtonV2>
              <ButtonV2 
                style={{ fontWeight: '500', padding: '0 1.5rem' }} 
                color='primary' 
                size="sm" 
                onClick={handleFileImport} 
                disabled={checkForDisable()}
              >
                Yes
              </ButtonV2>
            </Stack>
          </div>
        </>
      }
    />
    </>
  );
}
